import type { ITemplateRepository } from "@/application/ports/repositories";
import type {
  CatalogResponse,
  DuplicateTemplateRequest,
  PaginatedTemplates,
  TemplateCreateRequest,
  TemplateDetail,
  TemplateFilters,
  TemplateImagePreviewResponse,
  TemplateImageUploadRequest,
  TemplateImageUploadResponse,
  TemplateUpdateRequest,
} from "@/domain/template";
import { getLogger } from "@/infrastructure/di/services";

import { apiClient } from "../client";

export class TemplateRepository implements ITemplateRepository {
  async getTemplates(filters?: TemplateFilters): Promise<PaginatedTemplates> {
    try {
      const params = new URLSearchParams();
      if (filters?.limit) params.append("limit", String(filters.limit));
      if (filters?.offset) params.append("offset", String(filters.offset));
      if (filters?.search) params.append("search", filters.search);
      if (filters?.type) params.append("type", filters.type);
      if (filters?.category) params.append("category", filters.category);
      if (filters?.language) params.append("language", filters.language);
      if (filters?.industry) params.append("industry", filters.industry);

      const query = params.toString();
      const url = query ? `/templates?${query}` : "/templates";

      const response = await apiClient.get<PaginatedTemplates>(url);
      return response.data;
    } catch (error) {
      getLogger().error("Error loading templates:", error);
      throw error;
    }
  }

  async getTemplate(uuid: string): Promise<TemplateDetail> {
    try {
      const response = await apiClient.get<TemplateDetail>(`/templates/${uuid}`);
      return response.data;
    } catch (error) {
      getLogger().error("Error loading template:", error);
      throw error;
    }
  }

  async createTemplate(data: TemplateCreateRequest): Promise<TemplateDetail> {
    try {
      const response = await apiClient.post<TemplateDetail>("/templates", data, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      getLogger().error("Error creating template:", error);
      throw error;
    }
  }

  async updateTemplate(uuid: string, data: TemplateUpdateRequest): Promise<TemplateDetail> {
    try {
      const response = await apiClient.put<TemplateDetail>(`/templates/${uuid}`, data, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      getLogger().error("Error updating template:", error);
      throw error;
    }
  }

  async duplicateTemplate(uuid: string, data: DuplicateTemplateRequest): Promise<TemplateDetail> {
    try {
      const response = await apiClient.post<TemplateDetail>(`/templates/${uuid}/duplicate`, data, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      getLogger().error("Error duplicating template:", error);
      throw error;
    }
  }

  async deleteTemplate(uuid: string): Promise<void> {
    try {
      await apiClient.delete(`/templates/${uuid}`);
    } catch (error) {
      getLogger().error("Error deleting template:", error);
      throw error;
    }
  }

  async getCatalog(): Promise<CatalogResponse> {
    try {
      const response = await apiClient.get<CatalogResponse>("/placeholders/catalog");
      return response.data;
    } catch (error) {
      getLogger().error("Error loading catalog:", error);
      throw error;
    }
  }

  async previewTemplate(uuid: string): Promise<Blob> {
    try {
      return await apiClient.getBlob(`/templates/${uuid}/preview`);
    } catch (error) {
      getLogger().error("Error previewing template:", error);
      throw error;
    }
  }

  async uploadTemplateImage(
    uuid: string,
    data: TemplateImageUploadRequest,
  ): Promise<TemplateImageUploadResponse> {
    try {
      const response = await apiClient.post<{
        src: string;
        signed_url: string;
      }>(
        `/templates/${uuid}/images`,
        { data_uri: data.dataUri },
        { headers: { "Content-Type": "application/json" } },
      );
      return { src: response.data.src, signedUrl: response.data.signed_url };
    } catch (error) {
      getLogger().error("Error uploading template image:", error);
      throw error;
    }
  }

  async getTemplateImagePreview(uuid: string, src: string): Promise<TemplateImagePreviewResponse> {
    try {
      const response = await apiClient.post<{ signed_url: string }>(
        `/templates/${uuid}/images/preview`,
        { src },
        { headers: { "Content-Type": "application/json" } },
      );
      return { signedUrl: response.data.signed_url };
    } catch (error) {
      getLogger().error("Error fetching template image preview URL:", error);
      throw error;
    }
  }
}
